import {ModalOverlay, ModalXCloseButton} from '@/components/ui/modal-atoms/ModalAtoms.tsx'
import {
    StyledConversationsModal,
    StyledConversationsModalBody,
    StyledConversationsModalBodyContainer,
    StyledConversationsModalBodyOverlay,
    StyledConversationsModalHeader,
    StyledConversationsModalSubheader,
    StyledConversationsModalFooter,
    StyledBorderlessComboboxInput
} from '@/features/conversations/components/conversations-modal/style.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox.tsx'
import {memo, useState, useEffect, useId} from 'react'
import {GroupMember} from '@/features/users/types'
import {Button} from '@/components/ui/button/Button.tsx'
import {PlusIcon, ArrowLeftIcon, SearchSmIcon, ChevronDownIcon} from '@/components/ui/icon/Icon'
import {ConversationsList} from '@/features/conversations/components/conversations-list/ConversationsList'
import {ConversationMessages} from '@/features/conversations/components/conversation-messages/ConversationMessages'
import {Conversation} from '@/features/conversations/types'
import {useTranslation} from 'react-i18next'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {useCreateConversation} from '@/features/conversations/query/useCreateConversation'
import type {CreateConversationPayload} from '@/features/conversations/http/conversations.http'
import {NewConversationForm} from '../new-conversation-form/NewConversationForm'
import {ConversationParticipants} from '../conversation-participants/ConversationParticipants'
import {ConversationContextHeader} from '../conversation-context-header/ConversationContextHeader'
import {useInputSearch} from '@/hooks/useInputSearch'
import {Text} from '@/components/ui/text/Text'
import {errorHandler} from '@/utilities/helpers'
import {useConversations} from '@/features/conversations/query/useConversations'
import {useConversation} from '@/features/conversations/query/useConversation'
import {usePermissions} from '@/features/auth/store/store'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {NotificationContextBar} from '@/features/notifications/components/notification-context-bar/NotificationContextBar'
import {useNotificationContextHostStore} from '@/features/notifications/store/useNotificationContextHostStore'
import {useNotificationWorkContext} from '@/features/notifications/hooks/useNotificationWorkContext'
import {getNotificationPatientScope} from '@/features/notifications/utils/notificationScope'

interface ConversationsModalProps {
    isOpen: boolean
    onClose: () => void
    /** Patient scope for the conversations, or null for internal conversations (no patient) */
    patientId: string | null
    initialConversationId?: string | null
    /**
     * The patient the conversations belong to, for the panel to name them itself instead of
     * relying on the page behind it: opened from a notification it covers that page entirely.
     * Undefined for internal conversations, where there is no patient to name.
     */
    patientName?: string
    onClearInitialConversation?: () => void
    /**
     * Lets this panel host the notification work-context bar in place of its own header.
     *
     * `searchParam` is the deep link this panel is opened by — the bar clears it in the same
     * navigation as the work context. `closeSurface` closes the panel's UI state *without*
     * touching the address, which is what keeps that a single navigation.
     */
    notificationContextHost?: {searchParam?: string; closeSurface: () => void}
}

const ConversationsModalComponent = ({
    isOpen,
    onClose,
    patientId,
    initialConversationId,
    patientName,
    onClearInitialConversation,
    notificationContextHost
}: ConversationsModalProps) => {
    const {t} = useTranslation()
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
    const [isCreatingNewConversation, setIsCreatingNewConversation] = useState(false)
    const [selectedMembers, setSelectedMembers] = useState<GroupMember[]>([])
    const {searchValue, onSearch} = useInputSearch()
    const permissions = usePermissions()
    const comboboxStore = useComboboxStore({
        setValue: onSearch
    })

    // The conversation the operator asked for from the work context's reason line, which the
    // deep link either never named or could not resolve. A new deep link supersedes it.
    const [requestedConversationId, setRequestedConversationId] = useState<string | null>(null)

    const {conversations, isLoading: areConversationsLoading} = useConversations(patientId, 30)

    // While this panel is open on the task's own scope it hosts the work-context bar itself
    // instead of being covered by the wide band — the operator who closed the panel to work on
    // the page behind it reopens the conversations and finds their task where they left it,
    // one click from the conversation it is about.
    //
    // Scope, not merely "this surface is open": both the panel and the task are identified by a
    // patient or the absence of one, so one comparison keeps a task about somewhere else out —
    // an internal conversation's bar has no business sitting on a patient's conversations,
    // describing a thread that panel cannot even show. Still form-agnostic: no notification
    // type is read here, only the destination the backend resolved.
    const hostId = useId()
    const claimNotificationContextHost = useNotificationContextHostStore(state => state.claim)
    const releaseNotificationContextHost = useNotificationContextHostStore(state => state.release)
    const {phase: notificationContextPhase, notification: contextNotification} = useNotificationWorkContext()
    const hasNotificationContext = notificationContextPhase !== 'idle'

    const hostSearchParam = notificationContextHost?.searchParam
    const isNotificationInScope =
        hasNotificationContext &&
        !!contextNotification &&
        getNotificationPatientScope(contextNotification) === patientId

    useEffect(() => {
        if (!isOpen || !isNotificationInScope) return
        claimNotificationContextHost(hostId, hostSearchParam ? [hostSearchParam] : [])
        return () => releaseNotificationContextHost(hostId)
    }, [
        isOpen,
        isNotificationInScope,
        hostId,
        hostSearchParam,
        claimNotificationContextHost,
        releaseNotificationContextHost
    ])

    // The list holds one page, so a deep link can point at a conversation that is not in it —
    // which used to land the operator on the list with nothing saying which conversation they
    // had been sent to. Fetching that one by id costs a request only in exactly that case.
    const targetConversationId = requestedConversationId ?? initialConversationId ?? null
    const isTargetConversationListed = conversations.some(conversation => conversation.id === targetConversationId)
    const {data: fetchedConversation} = useConversation(
        isOpen && targetConversationId && !areConversationsLoading && !isTargetConversationListed
            ? targetConversationId
            : null
    )

    // A new deep link supersedes whatever the operator asked for on the previous one.
    useEffect(() => {
        setRequestedConversationId(null)
    }, [initialConversationId])

    useEffect(() => {
        // Also switches to a different conversation when the target changes while the modal is
        // already open (e.g. clicking another conversation notification)
        if (!targetConversationId || !isOpen || selectedConversation?.id === targetConversationId) return

        const targetConversation =
            conversations.find(conversation => conversation.id === targetConversationId) ??
            // Only when the fetched conversation belongs to this panel's scope: an older
            // notification without a resolvable link opens the internal drawer, and showing a
            // patient's conversation there would read and mark its messages against the wrong one.
            (fetchedConversation?.patientId === patientId ? fetchedConversation : undefined)

        if (targetConversation) {
            setSelectedConversation(targetConversation)
        }
    }, [targetConversationId, conversations, fetchedConversation, patientId, isOpen, selectedConversation])

    useEffect(() => {
        if (!isOpen) {
            setSelectedConversation(null)
            setIsCreatingNewConversation(false)
            setSelectedMembers([])
            setRequestedConversationId(null)
        }
    }, [isOpen])

    const createConversationMutation = useCreateConversation({
        onSuccess: newConversation => {
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CONVERSATIONS, patientId]})
            setIsCreatingNewConversation(false)
            setSelectedConversation(newConversation)
        },
        onError: errorHandler
    })

    const handleCreateNewConversation = () => {
        setSelectedMembers([])
        setIsCreatingNewConversation(true)
    }

    const handleCancelNewConversation = () => {
        setIsCreatingNewConversation(false)
        setSelectedMembers([])
    }

    const handleSubmitNewConversation = (
        data: {
            title: string
            message: string
            memberIds: string[]
            isUrgent: boolean
            entityId?: string
        },
        members: {type: 'user' | 'group'; id: string}[]
    ) => {
        const payload: CreateConversationPayload = {
            // Internal conversations (patientId null) are created without a patient
            ...(patientId !== null && {patientId}),
            title: data.title,
            message: data.message,
            isUrgent: data.isUrgent,
            members: members,
            entityId: data.entityId
        }

        createConversationMutation.mutate(payload)
    }

    const handleSelectConversation = (conversation: Conversation) => {
        setSelectedConversation(conversation)
    }

    const handleDeleteConversation = (conversationId: string) => {
        if (requestedConversationId === conversationId) {
            setRequestedConversationId(null)
        }
        if (selectedConversation?.id === conversationId) {
            setSelectedConversation(null)
        }
    }

    const handleBackToList = () => {
        setSelectedConversation(null)
        setIsCreatingNewConversation(false)
        setSelectedMembers([])
        // Clear the store value too, otherwise the input keeps showing the previous search text
        comboboxStore.setValue('')
        setRequestedConversationId(null)
        onSearch('')

        if (onClearInitialConversation) {
            onClearInitialConversation()
        }
    }

    if (!isOpen) return null

    // The panel's own name, not the open conversation's: a conversation is identified below
    // by kind, title and patient together, where the work-context bar cannot take its place.
    const title = patientId === null ? t('conversations:internal_title') : t('conversations:title')

    // Falls back to the notification's own copy of the name for the case where the panel is on
    // its list and cannot supply one — the deep link resolved to nothing, or carried no id.
    const contextTitle = selectedConversation?.title ?? contextNotification?.notification.extraData?.title ?? null
    const shouldShowContextHeader = Boolean(selectedConversation) || isNotificationInScope

    // Same pair the notification list navigates by, so the panel opens exactly the conversation
    // clicking the notification would have.
    const contextConversationId =
        contextNotification?.notification.extraData?.conversationId ?? contextNotification?.conversationId ?? null
    const canOpenContextConversation =
        isNotificationInScope && !!contextConversationId && selectedConversation?.id !== contextConversationId

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <StyledConversationsModal width="500px" onClick={event => event.stopPropagation()}>
                <Flexbox direction="column" fullWidth>
                    {isNotificationInScope ? (
                        <NotificationContextBar
                            variant="hosted"
                            trailingSlot={<ModalXCloseButton onClick={onClose} />}
                            onCloseHost={notificationContextHost?.closeSurface}
                        />
                    ) : (
                        <StyledConversationsModalHeader direction="column" gap={4} fullWidth>
                            <Flexbox justify="space-between" align="center" fullWidth>
                                <Flexbox align="center" gap={2}>
                                    {selectedConversation && (
                                        <Button variant="ghost" shape="square" onClick={handleBackToList}>
                                            <ArrowLeftIcon />
                                        </Button>
                                    )}
                                    <Text size="textXl" palette="neutral" shade="900" weight="500">
                                        {title}
                                    </Text>
                                </Flexbox>
                                <ModalXCloseButton onClick={onClose} />
                            </Flexbox>
                        </StyledConversationsModalHeader>
                    )}
                    {shouldShowContextHeader && (
                        <ConversationContextHeader
                            kind={patientId === null ? 'internal' : 'patient'}
                            title={contextTitle}
                            patientName={patientName}
                            notification={isNotificationInScope ? contextNotification : undefined}
                            onOpenConversation={
                                canOpenContextConversation
                                    ? () => setRequestedConversationId(contextConversationId)
                                    : undefined
                            }
                        />
                    )}
                    <StyledConversationsModalSubheader align="center" direction="row" fullWidth>
                        {!selectedConversation ? (
                            <Flexbox direction="column" fullWidth>
                                <ComboboxProvider store={comboboxStore} resetValueOnHide>
                                    <Flexbox fullWidth>
                                        <StyledBorderlessComboboxInput
                                            typeIcon={<SearchSmIcon size={16} />}
                                            placeholder={t('conversations:search_placeholder')}
                                            inputSize="sm"
                                            disabled={isCreatingNewConversation}
                                        />
                                    </Flexbox>
                                </ComboboxProvider>
                            </Flexbox>
                        ) : (
                            <ConversationParticipants
                                conversationId={selectedConversation.id}
                                participants={selectedConversation.members}
                                patientId={patientId}
                            />
                        )}
                    </StyledConversationsModalSubheader>
                </Flexbox>

                <StyledConversationsModalBodyContainer>
                    <StyledConversationsModalBody>
                        {selectedConversation ? (
                            <ConversationMessages
                                conversationId={selectedConversation.id}
                                hasUnreadMessages={selectedConversation.hasUnreadMessages}
                                patientId={patientId}
                            />
                        ) : (
                            <ConversationsList
                                patientId={patientId}
                                onSelectConversation={handleSelectConversation}
                                selectedConversationId={undefined}
                                search={searchValue}
                                onDeleteConversation={handleDeleteConversation}
                            />
                        )}
                    </StyledConversationsModalBody>
                    {isCreatingNewConversation && !selectedConversation && <StyledConversationsModalBodyOverlay />}
                </StyledConversationsModalBodyContainer>

                {!selectedConversation && permissions?.conversations?.write && (
                    <StyledConversationsModalFooter justify="center">
                        {isCreatingNewConversation ? (
                            <Flexbox direction="column" fullWidth gap={2} align="end">
                                <Button size="xs" variant="ghost" onClick={() => setIsCreatingNewConversation(false)}>
                                    {t('commons:close')}
                                    <ChevronDownIcon />
                                </Button>
                                <NewConversationForm
                                    patientId={patientId}
                                    selectedMembers={selectedMembers}
                                    onMembersChange={setSelectedMembers}
                                    onCancel={handleCancelNewConversation}
                                    onSubmit={handleSubmitNewConversation}
                                    isPendingCreatingNewConversation={createConversationMutation.isPending}
                                />
                            </Flexbox>
                        ) : (
                            <Button fullWidth variant="secondary" onClick={handleCreateNewConversation}>
                                <PlusIcon />
                                {t('conversations:new_cta')}
                            </Button>
                        )}
                    </StyledConversationsModalFooter>
                )}
            </StyledConversationsModal>
        </>
    )
}

export const ConversationsModal = memo(ConversationsModalComponent)
